// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import {selx} from 'clsx';

import {assertUnreachable} from 'common/util/typescript';
import {formatText} from 'common/util/format-text';
import {SvgIconEl} from 'common/components/svg-icon';
import {SpinnerEl} from 'common/components/spinner/panel';
import {SpinnerSize} from 'common/components/spinner/types';

import {MarkdownEl} from '../markdown/panel';
import type {MPWarning} from './';
import {WarningTheme} from './constants';

function Icon({theme, imgSrc}: {theme: WarningTheme; imgSrc: string}) {
  if (imgSrc) {
    return <img attrs={{src: imgSrc}} />;
  }
  switch (theme) {
    case WarningTheme.Error:
      return <SvgIconEl attrs={{icon: `warning`}} />;
    case WarningTheme.Sensitive:
      return <SvgIconEl attrs={{icon: `lock`}} />;
    case WarningTheme.NoData:
      return <div sel='.mp-warning-no-data-svg' />;
    case WarningTheme.NoIcon:
      return null;
    case WarningTheme.Default:
      return <div sel='.mp-warning-laptop-svg' />;
    case WarningTheme.Loading:
      return <SpinnerEl size={SpinnerSize.Large} />;
    case WarningTheme.Tip:
      return null;
    default:
      assertUnreachable(theme);
  }
}

function BodyText({text}: {text: string}) {
  if (!text) {
    return null;
  }

  return (
    <div sel='.body-text' attrs={{elref: `bodyText`}}>
      {text.split(`\n`).map((line, idx) => (
        <>
          {idx ? <br /> : null}
          {formatText(line)}
        </>
      ))}
    </div>
  );
}

export function Template(this: MPWarning) {
  const theme = this.attr(`theme`);
  const href = this.attr(`cta-href`);
  const imgSrc = this.attr(`img-src`);
  const bodyText = this.attr(`body-text`);
  const isSmall = !!this.attr(`small`);
  const themeClass = theme === WarningTheme.Tip ? {'theme-tip': true} : {};

  return (
    <div sel={selx(`.mp-warning-container`, {'.small': isSmall})}>
      <div sel={selx(`.mp-warning-content`, {'.small': isSmall})}>
        <Icon theme={theme} imgSrc={imgSrc} />
        {this.attr(`header-text`) && (
          <h1 attrs={{elref: `headerText`}} class={themeClass}>
            {this.attr(`header-text`)}
          </h1>
        )}
        {theme === WarningTheme.Tip ? (
          //Needed for Tips to have rich formatting
          <MarkdownEl
            text={bodyText}
            style={{
              color: `var(--primary-text-color)`,
              display: `block`,
              fontSize: `16px`,
              fontWeight: 400,
              height: `44px`,
              lineHeight: `22px`,
              maxWidth: `400px`,
            }}
          />
        ) : (
          //TODO verify and see if we can use MarkdownEl for all theme types
          <BodyText text={bodyText} />
        )}
      </div>
      <slot />
      {this.attr(`cta-text`) && (
        <a sel='.mp-warning-cta' attrs={{href}} on={{click: href ? null : this.helpers.handleCtaClick}}>
          {this.attr(`cta-text`)}
          <SvgIconEl attrs={{icon: `arrow-right`}} />
        </a>
      )}
    </div>
  );
}
